/**
 * Service Layer - Stream Service
 * Handles video and audio streaming logic
 * Uses Core layer for video encoding/decoding
 * Updates State layer (store)
 */

import {
    createVideoEncoder,
    configureVideoEncoder,
    encodeVideoFrame,
    createVideoDecoder,
    configureVideoDecoder,
    decodeVideoChunk,
    getFrameInfo,
} from '../core/webrtc/VideoCodec';
import {
    createAudioBuffer,
    createAudioContext,
    decompressAudio,
    int16ToFloat32,
    playAudioBuffer,
    splitBufferToInfoAndData,
} from '../core';
import {getVerticallyFlippedCanvas, drawRotatedFrame} from '../core';
import {
    combineArrayBuffers,
    numberToArrayBuffer,
    stringToArrayBuffer
} from '../core';
import {messageTypeEnum, KEY_FRAME_INTERVAL} from '../core';
import Utility from '../../../Utility';

export class StreamService {
    constructor(store, socketManager) {
        this.store = store;
        this.socketManager = socketManager;
        this.isTalking = false;
        this.soundArray = [];
        this.frequency = 48000;
    }

    /**
     * Start video encoding and sending
     */
    startVideoSending() {
        const myVideoChannel = `dynamic${this.store.deviceId}High`;
        this.store.frameNumber = 0;

        const onOutput = async (chunk) => {
            const end = Date.now();
            const duration = end - chunk.timestamp;
            const frameNumber = chunk.frameNumber;
            const videoMessage = this.frameToVideoMessage(chunk, frameNumber);

            const targetDelay = 33;
            if (duration < targetDelay) {
                await Utility.sleep(targetDelay - duration);
            }

            this.socketManager.sendVideoData(myVideoChannel, videoMessage);
            this.sendFrameDataMessage(frameNumber);
            this.store.frameNumber++;
            this.encodeFrame();
        };

        const onError = (error) => {
            console.error(`VideoEncoder error: ${error.message}`);
        };

        this.store.videoEncoder = createVideoEncoder(onOutput, onError);
        this.encodeFrame();
    }

    /**
     * Encode a single video frame
     */
    encodeFrame() {
        try {
            if (!this.store.shareScreenOn && !this.store.cameraOn) {
                return;
            }

            if (!this.store.videoEncoder) {
                return;
            }

            configureVideoEncoder(
                this.store.videoEncoder,
                this.store.streamCanvasContext.canvas.width,
                this.store.streamCanvasContext.canvas.height
            );

            const streamCanvas = getVerticallyFlippedCanvas(
                this.store.streamCanvasContext.canvas,
                !this.store.shareScreenOn,
                false
            );

            let isKeyFrame = !(this.store.frameNumber % KEY_FRAME_INTERVAL);
            if (this.store.isNextFrameKey) {
                this.store.isNextFrameKey = false;
                isKeyFrame = true;
            }

            encodeVideoFrame(this.store.videoEncoder, streamCanvas, Date.now(), isKeyFrame);
        } catch (e) {
            console.error('Error encoding frame:', e);
            if (this.store.videoEncoder) {
                this.store.videoEncoder.close();
                this.store.videoEncoder = null;
            }
            this.startVideoSending();
        }
    }

    /**
     * Convert video frame to message format
     */
    frameToVideoMessage(chunk, frameNumber) {
        const arrayBuffer = new ArrayBuffer(chunk.byteLength);
        chunk.copyTo(arrayBuffer);

        const frameInfoStr = JSON.stringify({
            Height: this.store.streamCanvasContext.canvas.height,
            Width: this.store.streamCanvasContext.canvas.width,
            Rotation: 0,
            User: {
                DeviceGuid: this.store.deviceId,
                Email: this.store.userDetails?.Email,
                DeviceName: 'Web Browser',
                Name: this.store.isGuest ? this.store.name : this.store.userDetails?.Name,
                LastName: this.store.userDetails?.LastName,
                id: this.store.userDetails?._id,
                sid: this.store.deviceId,
            },
            frameId: frameNumber,
            SenderSocketId: this.store.deviceId,
            sid: this.store.deviceId,
        });

        const frameInfoArrayBuffer = stringToArrayBuffer(frameInfoStr);
        const frameInfoSize = frameInfoArrayBuffer.byteLength;
        const frameInfoSizeBuffer = numberToArrayBuffer(frameInfoSize);

        return combineArrayBuffers([frameInfoSizeBuffer, frameInfoArrayBuffer, arrayBuffer]);
    }

    /**
     * Send frame data message (metadata about the frame)
     */
    sendFrameDataMessage(frameNumber) {
        const fullFrame = JSON.stringify({
            DeviceGUID: this.store.deviceId,
            IsWeb: true,
            DeviceName: 'Web Browser',
            FrameId: frameNumber,
            IsGuest: this.store.isGuest,
            IsVideo: true,
            ScreenOrientation: 0,
            SupportArSystemType: 0,
            MessageId: messageTypeEnum.CAMERA_FEED,
            IsScreenShare: this.store.shareScreenOn,
            SessionID: this.store.deviceId,
            sid: this.store.deviceId,
            isMuted: !this.store.micOn
        });

        this.socketManager.sendRoomMessage(this.store.roomId, fullFrame, this.store.deviceId);
    }

    /**
     * Stop video sending
     */
    stopVideoSending() {
        if (this.store.videoEncoder) {
            this.store.videoEncoder.close();
            this.store.videoEncoder = null;
        }
    }

    /**
     * Create decoder for a participant
     */
    createParticipantDecoder(slot, participantId) {
        const onOutput = (frame) => {
            this.renderFrame(slot, frame, participantId);
        };

        const onError = (error) => {
            console.error("Decoding error:", error);
        };

        const decoder = createVideoDecoder(onOutput, onError);
        configureVideoDecoder(decoder);

        return decoder;
    }

    /**
     * Handle received video data
     */
    handleReceivedVideoData(vp8Buffer) {
        const {dataBuffer, frameInfo} = splitBufferToInfoAndData(vp8Buffer);
        const {isKeyFrame, width, height} = getFrameInfo(dataBuffer);
        const participantId = frameInfo.sid;

        if (participantId === this.store.deviceId) {
            return;
        }

        if (this.store.remoteFreeze && participantId === this.store.biggerWindowConnectionId) {
            return;
        }

        const participant = this.store.participantsMap.get(participantId);
        if (!participant) {
            return;
        }

        // Update participant dimensions if this is a keyframe
        if (width) {
            participant.width = width;
            participant.height = height;
            participant.isWeb = this.store.latestFramesObj[participantId]?.IsWeb;
            participant.isScreenShare = this.store.latestFramesObj[participantId]?.IsScreenShare;
            this.store.participantsMap.set(participantId, participant);
        }

        if (!participant.width) {
            return;
        }

        try {
            decodeVideoChunk(participant.decoder, dataBuffer, isKeyFrame, Date.now());
        } catch (e) {
            console.error(e);
            // Recreate decoder on error
            participant.decoder = this.createParticipantDecoder(participant.slot, participantId);
        }
    }

    /**
     * Render decoded frame to canvas
     */
    renderFrame(slot, frame, participantId) {
        try {
            const canvasRef = this.store.canvasRefs.current[slot];
            const frameInfo = this.store.latestFramesObj[participantId];
            const needRotation = [1, 2].includes(frameInfo?.ScreenOrientation);

            if (needRotation) {
                drawRotatedFrame(canvasRef, frame);
            } else {
                const context = canvasRef.getContext("2d");
                context.drawImage(frame, 0, 0, canvasRef.width, canvasRef.height);
            }

            // Also render to big canvas if this is the bigger window
            if (participantId === this.store.biggerWindowConnectionId && this.store.bigCanvasRef?.current) {
                if (needRotation) {
                    drawRotatedFrame(this.store.bigCanvasRef.current, frame);
                } else {
                    const context = this.store.bigCanvasRef.current.getContext("2d");
                    context.drawImage(frame, 0, 0, this.store.bigCanvasRef.current.width, this.store.bigCanvasRef.current.height);
                }
            }

            frame.close();
        } catch (e) {
            console.error('Error rendering frame:', e);
        }
    }

    /**
     * Handle received audio data
     */
    handleReceivedAudioData(audioBuffer) {
        const {dataBuffer, frameInfo} = splitBufferToInfoAndData(audioBuffer);
        const participantId = frameInfo.sid;

        if (participantId === this.store.deviceId) {
            return;
        }

        if (dataBuffer[0] === 0) {
            this.store.activeAudiosTimes[participantId] = Date.now();
            this.store.activeAudios[participantId] = true;

            // Extract frequency
            const frequencyArr = dataBuffer.slice(1, 5);
            this.frequency = ((frequencyArr[frequencyArr.length - 4]) |
                (frequencyArr[frequencyArr.length - 3] << 8) |
                (frequencyArr[frequencyArr.length - 2] << 16) |
                (frequencyArr[frequencyArr.length - 1] << 24));

            // Decompress audio
            const newBuffer = dataBuffer.slice(5);
            const uncompressed = decompressAudio(newBuffer);
            const byte16 = new Int16Array(uncompressed.buffer);
            const floats = int16ToFloat32(byte16);

            this.soundArray.push(...floats);
        }
    }

    /**
     * Play accumulated audio
     */
    playAudio() {
        if (!this.soundArray.length || this.isTalking) {
            return;
        }

        if (!this.store.outputAudioCtx) {
            this.store.outputAudioCtx = createAudioContext();
        }

        const buffer = createAudioBuffer(
            this.store.outputAudioCtx,
            this.soundArray,
            this.frequency
        );

        this.isTalking = true;

        playAudioBuffer(this.store.outputAudioCtx, buffer, () => {
            this.isTalking = false;
        });

        this.soundArray = [];
    }

    /**
     * Request keyframe from participant
     */
    requestKeyFrame(participantId) {
        if (!participantId) {
            return;
        }
        this.socketManager.requestKeyframe(participantId, this.store.deviceId);
    }

    /**
     * Cleanup
     */
    cleanup() {
        this.stopVideoSending();
        this.soundArray = [];
        this.isTalking = false;
    }
}
